import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { recognitionAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Loader2, Plus, Pencil, Copy, Archive, Users, ArrowLeft, Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { PROGRAM_STATUS_META } from './RecognitionPortalPage';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function RecognitionAdminPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEdition, setNewEdition] = useState('');
  const [busy, setBusy] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const load = () => {
    setLoading(true);
    recognitionAPI.listPrograms()
      .then((r) => setPrograms(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) { toast.error('Program name is required'); return; }
    setBusy(true);
    try {
      const r = await recognitionAPI.createProgram({ name: newName, edition: newEdition || null });
      toast.success('Program created');
      setShowCreate(false); setNewName(''); setNewEdition('');
      navigate(`/recognition/admin/${r.data.program_id}`);
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };

  const duplicate = async (id) => {
    try { const r = await recognitionAPI.duplicateProgram(id); toast.success('Program duplicated'); navigate(`/recognition/admin/${r.data.program_id}`); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };

  const archive = async () => {
    try { await recognitionAPI.archiveProgram(archiveTarget.program_id); toast.success('Program archived'); setArchiveTarget(null); load(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <>
      <PageHeader
        title="Manage Recognition Programs"
        subtitle="Create and manage corporate recognition events and incentive programs"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/recognition')} data-testid="recognition-view-portal">
              <ArrowLeft size={16} className="mr-2" /> Portal
            </Button>
            <Button onClick={() => setShowCreate(true)} data-testid="recognition-create-btn">
              <Plus size={16} className="mr-2" /> New Program
            </Button>
          </div>
        }
      />
      <div className="page-content">
        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin" size={28} /></div>
        ) : programs.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-slate-500">
            <p className="font-semibold text-slate-700">No programs yet</p>
            <p className="text-sm">Create your first recognition program.</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {programs.map((p) => {
              const meta = PROGRAM_STATUS_META[p.status] || PROGRAM_STATUS_META.coming_soon;
              return (
                <Card key={p.program_id} className="overflow-hidden" data-testid={`recognition-admin-row-${p.program_id}`}>
                  <CardContent className="py-3 flex items-center gap-4">
                    <div className="w-28 h-16 shrink-0 rounded-md overflow-hidden bg-slate-100 border">
                      {p.cover_image_url ? <img src={`${BACKEND}${p.cover_image_url}`} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-400">No cover</div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{p.name}</span>
                        {p.edition && <span className="text-xs text-slate-400">{p.edition}</span>}
                        <Badge className={`border text-[10px] ${meta.cls}`}>{meta.label}</Badge>
                        <Badge variant={p.published ? 'default' : 'secondary'} className="text-[10px]">
                          {p.published ? <><Eye size={10} className="mr-1" />Published</> : <><EyeOff size={10} className="mr-1" />Draft</>}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Users size={12} /> {p.participant_count || 0} participants</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/recognition/admin/${p.program_id}`)} data-testid={`recognition-edit-${p.program_id}`}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => duplicate(p.program_id)} data-testid={`recognition-dup-${p.program_id}`}><Copy size={14} /></Button>
                      <Button variant="ghost" size="sm" className="text-amber-600" onClick={() => setArchiveTarget(p)} data-testid={`recognition-archive-${p.program_id}`}><Archive size={14} /></Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Recognition Program</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Program name *</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. THE ESCAPE 2027" data-testid="new-program-name" /></div>
            <div className="space-y-1.5"><Label>Edition</Label>
              <Input value={newEdition} onChange={(e) => setNewEdition(e.target.value)} placeholder="e.g. Inaugural Edition" data-testid="new-program-edition" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy} data-testid="new-program-save">{busy ? <Loader2 size={15} className="animate-spin mr-1" /> : null}Create & Edit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!archiveTarget} onOpenChange={() => setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Archive Program</AlertDialogTitle>
            <AlertDialogDescription>“{archiveTarget?.name}” will be hidden from agents and moved to archived. You can still view it here.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={archive} className="bg-amber-600 hover:bg-amber-700">Archive</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
